import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { FormGroup, Input, Label } from 'reactstrap';

import CollapsibleCard from '../../common/CollapsibleCard';
import OtoToggle from '../../common/OtoToggle';
import { IPlace } from 'types';
import { ReservationCalendarPreferences } from 'store/reservation-calendar';
import './ReservationsViewOptions.scss';
import ReservationsPlaceSelect from '../ReservationsPlaceSelect';

const calendarGridOpenHours = [
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
].map((hourStr) => `${hourStr}:00`);

const calendarGridCloseHours = [
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
].map((hourStr) => `${hourStr}:00`);

interface IProps {
  children?: React.ReactNode;
  openByDefault?: boolean;
  places: IPlace[];
  isPlacesLoading: boolean;
  preferences: ReservationCalendarPreferences;
  showCalendarViewOptions?: boolean;
  showMapViewOptions?: boolean;
  showPlaceSelect: boolean;
  showHours: boolean;
  updatePreferences: (
    preferences: Partial<ReservationCalendarPreferences>
  ) => void;
}

export const ReservationsViewOptions: React.FC<IProps> = (props) => {
  const {
    children,
    openByDefault,
    places,
    isPlacesLoading,
    preferences,
    showCalendarViewOptions = true,
    showMapViewOptions = false,
    showHours,
    showPlaceSelect,
    updatePreferences,
  } = props;

  const onActivePlaceChange = (place: IPlace | null) =>
    updatePreferences({ activePlace: place });

  const onMaxTimeChange = (e) => updatePreferences({ timeTo: e.target.value });
  const onMinTimeChange = (e) =>
    updatePreferences({ timeFrom: e.target.value });

  const onHideDisabledPlacesChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    updatePreferences({
      hideDisabledPlaces: e.target.checked,
    });

  const {
    activePlace,
    hideDisabledPlaces,
    showDeclinedReservations,
    showPeopleAmountOnMap,
    showReservationNotes,
    showReservationIfPresentOnMap,
    timeFrom,
    timeTo,
  } = preferences;

  return (
    <CollapsibleCard
      className="no-print"
      openByDefault={openByDefault}
      title={
        <div className="calendar-preferences">
          <div className="mr-2">{i18next.t('reservations-view-options.title')}</div>
          {showHours && (
            <>
              <span className="calendar-preferences__item">
                {timeFrom} - {timeTo}
              </span>
            </>
          )}
          <span className="calendar-preferences__item">
            {activePlace ? activePlace.name : i18next.t('reservations-view-options.all-rooms')}
          </span>
          {showCalendarViewOptions && (
            <>
              <span className="calendar-preferences__item">
                {i18next.t('reservations-view-options.notes-visible')}: {showReservationNotes ? i18next.t('Yes') : i18next.t('No')}
              </span>
              <span className="calendar-preferences__item">
                {i18next.t('reservations-view-options.declined-visible')}:{' '}
                {showDeclinedReservations ? i18next.t('Yes') : i18next.t('No')}
              </span>
            </>
          )}
          {showMapViewOptions && (
            <>
              <span className="calendar-preferences__item">
                {i18next.t('reservations-view-options.table-sizes')}: {showPeopleAmountOnMap ? i18next.t('Yes') : i18next.t('No')}
              </span>
              <span className="calendar-preferences__item">
                {i18next.t('reservations-view-options.reservations-on-tables')}:{' '}
                {showReservationIfPresentOnMap ? i18next.t('Yes') : i18next.t('No')}
              </span>
            </>
          )}
        </div>
      }
    >
      <>
        {showHours && (
          <FormGroup className="bb-1 bc-grey pb-3 mb-2">
            <div>
              <Label className="d-block font-weight-bold">
                {i18next.t('reservations-view-options.hour-range-label')}
                <div className="small">
                  {i18next.t('reservations-view-options.hour-range-hint')}
                </div>
              </Label>
            </div>
            <div className="d-flex align-vertical mt-2" style={{ gap: 10 }}>
              <Label className="m-0 p-0" htmlFor="calendarMinHour">
                {i18next.t('reservations-view-options.from')}
              </Label>
              <Input
                type="select"
                className="mw-150"
                id="calendarMinHour"
                value={timeFrom}
                onChange={onMinTimeChange}
              >
                {calendarGridOpenHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))}
              </Input>
              <Label className="m-0 p-0" htmlFor="calendarMaxHour">
                {i18next.t('reservations-view-options.to')}
              </Label>
              <Input
                type="select"
                className="mw-150"
                id="calendarMaxHour"
                value={timeTo}
                onChange={onMaxTimeChange}
              >
                {calendarGridCloseHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))}
              </Input>
            </div>
          </FormGroup>
        )}

        {showPlaceSelect && (
          <ReservationsPlaceSelect
            activePlace={activePlace}
            onActivePlaceChange={onActivePlaceChange}
            places={places}
            isPlacesLoading={isPlacesLoading}
          />
        )}
        {showCalendarViewOptions && (
          <>
            <div>
              <OtoToggle
                className="align-vertical"
                label={i18next.t('reservations-view-options.show-notes-toggle')}
                labelWrapperClassName="d-inline-flex align-vertical font-weight-bold mt-2"
                checked={showReservationNotes}
                onChange={(e) =>
                  updatePreferences({
                    showReservationNotes: e.target.checked,
                  })
                }
              />
            </div>
            <div>
              <OtoToggle
                className="align-vertical"
                label={i18next.t('reservations-view-options.show-declined-toggle')}
                labelWrapperClassName="d-inline-flex align-vertical font-weight-bold mt-2"
                checked={showDeclinedReservations}
                onChange={(e) =>
                  updatePreferences({
                    showDeclinedReservations: e.target.checked,
                  })
                }
              />
            </div>
            <div>
              <OtoToggle
                className="align-vertical"
                label={i18next.t('reservations-view-options.hide-disabled-places-toggle')}
                labelWrapperClassName="d-inline-flex align-vertical font-weight-bold mt-2"
                checked={hideDisabledPlaces}
                onChange={onHideDisabledPlacesChange}
              />
            </div>
            <hr />
          </>
        )}
        <h3>{i18next.t('reservations-view-options.map-settings-title')}</h3>
        <OtoToggle
          className="align-vertical"
          label={i18next.t('reservations-view-options.show-table-sizes-toggle')}
          labelWrapperClassName="d-inline-flex align-vertical font-weight-bold mt-2"
          checked={showPeopleAmountOnMap}
          onChange={(e) =>
            updatePreferences({ showPeopleAmountOnMap: e.target.checked })
          }
        />
        <OtoToggle
          className="align-vertical"
          label={i18next.t('reservations-view-options.show-reservations-on-tables-toggle')}
          labelWrapperClassName="d-inline-flex align-vertical font-weight-bold mt-2"
          checked={showReservationIfPresentOnMap}
          onChange={(e) =>
            updatePreferences({
              showReservationIfPresentOnMap: e.target.checked,
            })
          }
        />
        <hr />
        {children}
      </>
    </CollapsibleCard>
  );
};

ReservationsViewOptions.propTypes = {
  children: PropTypes.node,
  showHours: PropTypes.bool.isRequired,
};

export default ReservationsViewOptions;
